import { z } from 'zod';
import { CAMPAIGN_LIMITS, isCalendarDate } from '../campaigns.js';
import { addCalendarDays, calendarDaysBetween } from '../ai/topic-planner.js';
import { THEME_SLOT_LIMITS } from './theme-slots.js';
/**
 * The year plan (spec 110 section 4.7, S150-5, decision E150-1): one
 * `year_planner` run drafts the theme slots of the next twelve months; the
 * rolling planner turns them into ideas just in time. The AI only adds slots;
 * it never changes or removes one and never creates a post.
 */
export const YEAR_PLAN_LIMITS = {
    /** Without a chosen period the run plans today .. today + 364 (tenant days): twelve months. */
    periodDays: 365,
    /** A chosen period (S150-10) spans 7 .. 365 days and starts today or within the next 365 days. */
    minPeriodDays: 7,
    maxPeriodDays: 365,
    maxStartAheadDays: 365,
    minSlots: 1,
    maxSlots: 60,
    /** One AI slot spans at most about a quarter. */
    maxSlotDays: 92,
    maxSummaryLength: 500,
    /** Existing slots the context carries at most. */
    maxExistingSlots: 200,
    maxCampaigns: 50,
    /** S240-1: campaigns one run may propose, and the length of a proposed goal. */
    maxProposedCampaigns: 12,
    maxCampaignGoalLength: 300,
    maxCampaignKeyLength: 40,
    /** Names of the property's non-archived campaigns overlapping the period the context carries at most. */
    maxExistingCampaignNames: 200,
};
const calendarDate = z.string().refine(isCalendarDate, { message: 'expected a calendar day YYYY-MM-DD' });
/** The run's parameters (subject `property`). */
export const yearPlannerInputSchema = z
    .object({
    property_id: z.uuid(),
    period_start: calendarDate,
    period_end: calendarDate,
})
    .strict()
    .refine((input) => input.period_end >= input.period_start, { message: 'period_end before period_start', path: ['period_end'] });
export const yearPlanSlotSchema = z
    .object({
    pillar_key: z.string().min(1).max(64),
    period_start: calendarDate,
    period_end: calendarDate,
    theme: z.string().trim().min(1).max(THEME_SLOT_LIMITS.themeMaxLength),
    campaign_id: z.uuid().nullable(),
    /** S240-1 (prompt v3): the key of a campaign proposed in the same output, else null. */
    campaign_key: z.string().min(1).max(YEAR_PLAN_LIMITS.maxCampaignKeyLength).nullable(),
})
    .strict();
/** S240-1: a campaign the year plan proposes; created as a draft together with the slots. */
export const yearPlanCampaignSchema = z
    .object({
    key: z.string().min(1).max(YEAR_PLAN_LIMITS.maxCampaignKeyLength),
    name: z.string().trim().min(1).max(CAMPAIGN_LIMITS.nameMaxLength),
    goal: z.string().trim().min(1).max(YEAR_PLAN_LIMITS.maxCampaignGoalLength),
    period_start: calendarDate,
    period_end: calendarDate,
})
    .strict();
export const yearPlannerOutputSchema = z
    .object({
    campaigns: z.array(yearPlanCampaignSchema).max(YEAR_PLAN_LIMITS.maxProposedCampaigns),
    slots: z.array(yearPlanSlotSchema).min(YEAR_PLAN_LIMITS.minSlots).max(YEAR_PLAN_LIMITS.maxSlots),
    summary: z.string().trim().max(YEAR_PLAN_LIMITS.maxSummaryLength),
})
    .strict();
/**
 * The period of a year plan (S150-10): the chosen one, or the twelve months
 * from `today` when neither day is given. Both days are inclusive tenant days.
 */
export function resolveYearPlanPeriod(today, start, end) {
    if (start === undefined && end === undefined)
        return { start: today, end: addCalendarDays(today, YEAR_PLAN_LIMITS.periodDays - 1) };
    if (start === undefined)
        return { issue: { field: 'period_start', code: 'period_incomplete' } };
    if (end === undefined)
        return { issue: { field: 'period_end', code: 'period_incomplete' } };
    if (!isCalendarDate(start))
        return { issue: { field: 'period_start', code: 'invalid_date' } };
    if (!isCalendarDate(end))
        return { issue: { field: 'period_end', code: 'invalid_date' } };
    if (start < today)
        return { issue: { field: 'period_start', code: 'period_in_past' } };
    if (calendarDaysBetween(today, start) > YEAR_PLAN_LIMITS.maxStartAheadDays)
        return { issue: { field: 'period_start', code: 'period_too_far' } };
    const days = calendarDaysBetween(start, end) + 1;
    if (days < YEAR_PLAN_LIMITS.minPeriodDays)
        return { issue: { field: 'period_end', code: 'period_too_short' } };
    if (days > YEAR_PLAN_LIMITS.maxPeriodDays)
        return { issue: { field: 'period_end', code: 'period_too_long' } };
    return { start, end };
}
/** Posts the rhythm yields over `days` inclusive days, rounded up. */
export function postsInPeriod(postsPerWeek, days) {
    return Math.ceil((postsPerWeek * days) / 7);
}
/** `credits_per_month` of the forecast: the chain price times the posts of an average month, rounded up. */
export function creditsPerMonth(creditsPerPost, postsPerWeek) {
    return Math.ceil((creditsPerPost * postsPerWeek * 52) / 12);
}
/**
 * The topic-planner runs the near-term step of a year plan is expected to queue
 * (spec 110 section 4.7, S240-1): over the part of the period inside today ..
 * today + `leadMaxDays` (its span in days as the rolling planner counts it, at
 * least 1), the posting target ceil(postsPerWeek x span / 7) spread at most
 * `ideasPerSlot` per slot, so ceil(target / ideasPerSlot) runs; 0 when rolling
 * planning is off or the period starts after the window.
 */
export function nearTermPlannerRuns(input) {
    if (!input.rollingEnabled)
        return 0;
    const windowEnd = addCalendarDays(input.today, input.leadMaxDays);
    const start = input.periodStart > input.today ? input.periodStart : input.today;
    const end = input.periodEnd < windowEnd ? input.periodEnd : windowEnd;
    if (end < start)
        return 0;
    const target = Math.ceil((input.postsPerWeek * Math.max(1, calendarDaysBetween(start, end))) / 7);
    return Math.ceil(target / input.ideasPerSlot);
}
//# sourceMappingURL=year-plan.js.map